import { Head } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"

type Lead = {
  id: number
  name: string
  email: string
  website: string
  spend_band: string
  interest: string
  message: string | null
  utm_source: string | null
  utm_campaign: string | null
  email_delivered: boolean
  email_error: string | null
  notion_synced: boolean
  notion_error: string | null
  created_at: string
}

const DESCRIPTION =
  "Every application-form submission, newest first, with email and Notion delivery status."

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

export default function AdminLeadsIndex({ leads }: { leads: Lead[] }) {
  return (
    <>
      <Head title="Leads">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Leads" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell wide>
        <PageHeader
          title="Leads"
          description={`${leads.length} ${leads.length === 1 ? "submission" : "submissions"}, newest first`}
        />

        {leads.length === 0 ? (
          <div className="mt-6 flex h-40 items-center justify-center rounded-lg border border-hairline bg-page">
            <p className="text-sm text-ink-muted">No leads yet.</p>
          </div>
        ) : (
          <div className="mt-6 overflow-hidden rounded-lg border border-hairline bg-page">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Lead</TableHeaderCell>
                  <TableHeaderCell>Spend band</TableHeaderCell>
                  <TableHeaderCell>Interest</TableHeaderCell>
                  <TableHeaderCell>Message</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                  <TableHeaderCell>Received</TableHeaderCell>
                  <TableHeaderCell>Delivery</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <div className="font-medium text-ink-display">{lead.name}</div>
                      <a href={`mailto:${lead.email}`} className="block text-xs">
                        {lead.email}
                      </a>
                      <a
                        href={lead.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-xs text-ink-muted"
                      >
                        {displayHost(lead.website)}
                      </a>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{lead.spend_band}</TableCell>
                    <TableCell className="whitespace-nowrap">{lead.interest}</TableCell>
                    <TableCell>
                      {lead.message ? (
                        <span
                          className="block max-w-[12rem] truncate"
                          title={lead.message}
                        >
                          {lead.message}
                        </span>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {lead.utm_source ? (
                        <>
                          <div>{lead.utm_source}</div>
                          {lead.utm_campaign && (
                            <div className="text-xs text-ink-muted">{lead.utm_campaign}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-ink-muted">Direct</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(lead.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <DeliveryBadge
                          label="Email"
                          ok={lead.email_delivered}
                          error={lead.email_error}
                        />
                        <DeliveryBadge
                          label="Notion"
                          ok={lead.notion_synced}
                          error={lead.notion_error}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function DeliveryBadge({
  label,
  ok,
  error,
}: {
  label: string
  ok: boolean
  error: string | null
}) {
  if (ok) return <Badge tone="accent">{label}</Badge>
  if (error) return <Badge tone="danger" title={error}>{label} failed</Badge>
  return <Badge tone="muted" title="Not delivered / not configured">{label}</Badge>
}

function displayHost(url: string) {
  try {
    return new URL(url).host.replace(/^www\./, "")
  } catch {
    return url
  }
}

function formatDateTime(iso: string) {
  const [date, timeRaw] = iso.split("T")
  const parts = date.split("-").map(Number)
  const y = parts[0]
  const m = parts[1]
  const d = parts[2]
  const hm = (timeRaw || "").slice(0, 5)
  const base = `${d} ${MONTHS[m - 1]} ${y}`
  return hm ? `${base}, ${hm}` : base
}
